import {
    Context,
    createContext,
    useState,
    useContext,
    useRef,
    useEffect,
    useCallback
} from "react";
import { ILoading, IError, useMountWithTriggers } from "xa-generics";
import { IDeliveryFilterForm } from "../Components/Delivery/Interfaces/IDeliveryFilterForm.interface";
import { DeliveryFilterForm } from "../Components/Delivery/Static/DeliveryFilter.form";
import { IDeliveryContext } from "../Interfaces/IDeliveryContext.interface";
import { CourierTurnModel } from "../Models/CourierTurn.model";
import { useDateFilter } from "./DateFilter.context";
import { ChangeHandler } from "../Utils/ChangeHandler.util";
import { IInputChange } from "xa-inputs";
import { useCouriers } from "./Courier.context";
import { cloneDeep } from "lodash";
import { useAccess } from "../Modules/Access/Context/Access.context";
import { RoundDAO } from "../Api/DAO/CourierTurn.dao";
import { ICoords } from "../Interfaces/ICoords.interface";

/**
 * ## DeliveryContext
 */
const DeliveryContext: Context<IDeliveryContext> = createContext<IDeliveryContext>(null as never);

DeliveryContext.displayName = "DeliveryContext";

interface IDeliveryContextWrapperProps {}

/**
 * ## Delivery context wrapper component
 *
 */
export const DeliveryContextWrapper: React.FC<IDeliveryContextWrapperProps> = (props) => {
    const { restaurant } = useAccess();
    const { couriersById } = useCouriers();
    const {
        form: { date }
    } = useDateFilter();

    const [deliveryForm, setForm] = useState<IDeliveryFilterForm>(cloneDeep(DeliveryFilterForm));
    const [list, setList] = useState<CourierTurnModel[]>([]);
    const [loading, setLoading] = useState<ILoading>(true);
    const [error, setError] = useState<IError>(null);

    const COORDS_TTL = 300_000; //5 minutes

    const coordsCacheRef = useRef<Record<string, { coords: ICoords; updatedAt: number }>>({});

    const updateCourierCoords = useCallback((turnId: string, coords: ICoords): void => {
        coordsCacheRef.current[turnId] = { coords, updatedAt: Date.now() };

        setList((current) => {
            const newList = cloneDeep(current);
            for (let index in newList) {
                if (newList[index].id === turnId) {
                    newList[index].couierCoords = coords;
                }
            }
            return newList;
        });
    }, []);

    const loadRounds = (): void => {
        setLoading(
            RoundDAO.getDeliveryRounds(restaurant.delay_time, date.value as Date)
                .then((unsortedList) => {
                    return unsortedList.sort((aTurn, bTurn) => {
                        const aCourier = aTurn.getCourier(couriersById);
                        const bCourier = bTurn.getCourier(couriersById);
                        if (!aCourier || !bCourier) return -1;
                        if (aCourier.name < bCourier.name) return -1;
                        if (aCourier.name > bCourier.name) return 1;
                        return 0;
                    });
                })
                .then((sortedTurns) => {
                    const now = Date.now();
                    for (let turn of sortedTurns) {
                        if (!turn.couierCoords) {
                            const cached = coordsCacheRef.current[turn.id];
                            if (cached && now - cached.updatedAt < COORDS_TTL) {
                                turn.couierCoords = cached.coords;
                            }
                        }
                    }
                    setList(sortedTurns);
                })
                .catch((error: IError) => setError(error))
                .finally(() => setLoading(false))
        );
    };

    useMountWithTriggers(loadRounds, [date]);

    // Cleanup expired cache entries every 15 seconds
    useEffect(() => {
        const interval = setInterval(() => {
            const now = Date.now();
            const cache = coordsCacheRef.current;
            const expiredTurnIds: string[] = [];

            for (const turnId in cache) {
                if (now - cache[turnId].updatedAt >= COORDS_TTL) {
                    expiredTurnIds.push(turnId);
                    delete cache[turnId];
                }
            }

            if (expiredTurnIds.length > 0) {
                setList((current) => {
                    const newList = cloneDeep(current);
                    for (let item of newList) {
                        if (expiredTurnIds.includes(item.id)) {
                            item.couierCoords = null;
                        }
                    }
                    return newList;
                });
            }
        }, 15_000);

        return () => clearInterval(interval);
    }, []);

    return (
        <DeliveryContext.Provider
            value={{
                onChange: (e: IInputChange) =>
                    setForm(ChangeHandler(deliveryForm, e.fieldName, e.value)),
                reloadRounds: loadRounds,
                updateCourierCoords,
                form: deliveryForm,
                setList,
                loading,
                error,
                list
            }}
        >
            {props.children}
        </DeliveryContext.Provider>
    );
};
export const useDelivery = () => useContext(DeliveryContext);
